import { useAtomValue, useSetAtom } from 'jotai';
import { type MutableRefObject, useCallback, useEffect } from 'react';

import type { MeasureBundleObject } from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum, type MeasureResponsesEntryMethod } from '../../segment/types';
import { measureResponsesEntryContextAtom, selectedClientInfoAtom } from '../state';

export interface MeasureResponsesTrackingContext {
  entryMethod?: MeasureResponsesEntryMethod;
  resetEntryMethod: () => void;
  trackingState: MutableRefObject<MeasureResponsesTrackingState>;
}

interface TrackMeasureResponsesDrawerViewOptions {
  appointmentShortId?: string;
  cssrsLoading: boolean;
  entryMethod?: MeasureResponsesEntryMethod;
  hasCssrsAlert: boolean;
  inSession: boolean;
  measureBundle?: MeasureBundleObject;
  measureBundleItemId?: string;
  patientShortId?: string;
  providerShortId?: string;
  resetEntryMethod: () => void;
  trackingState: MutableRefObject<MeasureResponsesTrackingState>;
}

export interface MeasureResponsesTrackingState {
  bundleIsVisible: boolean;
  hasTrackedInitialBundle: boolean;
  patientShortId?: string;
  previousBundleShortId?: string;
}

export function createMeasureResponsesTrackingState(): MeasureResponsesTrackingState {
  return {
    bundleIsVisible: false,
    hasTrackedInitialBundle: false,
  };
}

export function useMeasureResponsesDrawerTrackingContext(
  hasSelectedBundle: boolean,
  trackingState: MutableRefObject<MeasureResponsesTrackingState>,
): MeasureResponsesTrackingContext {
  const patientShortId = useAtomValue(selectedClientInfoAtom)?.patientShortId;
  const entryContext = useAtomValue(measureResponsesEntryContextAtom);
  const setEntryContext = useSetAtom(measureResponsesEntryContextAtom);
  const resetEntryMethod = useCallback(() => setEntryContext(undefined), [setEntryContext]);
  const entryMethod =
    entryContext && entryContext.patientShortId === patientShortId ? entryContext.entryMethod : undefined;

  useEffect(
    function clearEntryContextForAnotherPatient() {
      if (entryContext && patientShortId && entryContext.patientShortId !== patientShortId) {
        setEntryContext(undefined);
      }
    },
    [entryContext, patientShortId, setEntryContext],
  );

  useEffect(
    function markBundleAsHiddenWhenListIsViewed() {
      if (!hasSelectedBundle) {
        trackingState.current.bundleIsVisible = false;
      }
    },
    [hasSelectedBundle, trackingState],
  );

  return { entryMethod, resetEntryMethod, trackingState };
}

export function useTrackMeasureResponsesDrawerView({
  appointmentShortId,
  cssrsLoading,
  entryMethod,
  hasCssrsAlert,
  inSession,
  measureBundle,
  measureBundleItemId,
  patientShortId,
  providerShortId,
  resetEntryMethod,
  trackingState,
}: TrackMeasureResponsesDrawerViewOptions) {
  const { userViewed } = useTrackEvent();

  useEffect(
    function trackMeasureResponsesDrawerView() {
      if (trackingState.current.patientShortId !== patientShortId) {
        trackingState.current = {
          bundleIsVisible: false,
          hasTrackedInitialBundle: false,
          patientShortId,
        };
      }

      if (!measureBundle) {
        trackingState.current.bundleIsVisible = false;
        return;
      }
      if (cssrsLoading || !patientShortId || !providerShortId) return;

      const measureBundleShortId = measureBundle.measureBundleShortId;
      if (
        trackingState.current.bundleIsVisible &&
        trackingState.current.previousBundleShortId === measureBundleShortId
      ) {
        return;
      }

      const selectedMeasure =
        measureBundle.measureBundleItems.find(
          (measureBundleItem) => measureBundleItem?.measureBundleItemId?.toString() === measureBundleItemId,
        ) ??
        measureBundle.measureBundleItems.find((measureBundleItem) => Boolean(measureBundleItem?.measureResponse)) ??
        measureBundle.measureBundleItems[0];
      const measureResponse = selectedMeasure?.measureResponse;

      userViewed(EventTypeEnum.MEASURE_RESPONSES_DRAWER_VIEWED, {
        patientShortId,
        providerShortId,
        measureBundleShortId,
        measureResponseShortId: measureResponse?.measureResponseShortId ?? null,
        measureSchemaType: selectedMeasure?.measureSchema?.type ?? null,
        scoreCategoryLabel: measureResponse?.scoreCategoryLabel ?? null,
        displayScore: measureResponse?.score ?? null,
        appointmentShortId,
        inSession,
        hasCssrsAlert,
        ...(trackingState.current.hasTrackedInitialBundle || !entryMethod ? {} : { entryMethod }),
        ...(trackingState.current.previousBundleShortId
          ? {
              previousMeasureBundleShortId: trackingState.current.previousBundleShortId,
            }
          : {}),
      });

      trackingState.current.bundleIsVisible = true;
      trackingState.current.previousBundleShortId = measureBundleShortId;
      if (!trackingState.current.hasTrackedInitialBundle) {
        trackingState.current.hasTrackedInitialBundle = true;
        resetEntryMethod();
      }
    },
    [
      appointmentShortId,
      cssrsLoading,
      entryMethod,
      hasCssrsAlert,
      inSession,
      measureBundle,
      measureBundleItemId,
      patientShortId,
      providerShortId,
      resetEntryMethod,
      trackingState,
      userViewed,
    ],
  );
}
